import { ErrorHandler, inject, Provider } from '@angular/core';

import { LoggerService } from '@ideals/core/logger';
import { LogRocketService } from '@ideals/core/logrocket';
import { SentryService } from '@ideals/core/sentry';
import { uniqueId } from '@ideals/utils/unique-id';

import { RequestError } from '../../models';
import { ErrorsService } from '../../services/errors';

import { isIgnoredError } from './ignored-errors';

const CHUNK_LOADING_ERRORS: RegExp[] = [
  /Loading chunk [\w-]+ failed/,
  /Failed to fetch dynamically imported module/,
];
const MEMOIZED_ERROR_STACK_LINES_COUNT = 3;

export class CustomErrorHandler implements ErrorHandler {
  readonly #errorsService = inject(ErrorsService);
  readonly #loggerService = inject(LoggerService);
  readonly #logRocketService = inject(LogRocketService);
  readonly #sentryService = inject(SentryService);

  readonly #loggedErrors = new Set<string>();

  handleError(error: unknown): void {
    if (error instanceof Error && isIgnoredError(error)) {
      return;
    }

    if (error instanceof Error && CHUNK_LOADING_ERRORS.some((chunkError) => chunkError.test(error.message))) {
      window.location.reload();

      return;
    }

    if (typeof error === 'object' && 'status' in error! && typeof error.status === 'string') {
      this.#errorsService.handleError(error as RequestError);

      return;
    }

    if (error instanceof Error) {
      const errorId = uniqueId();

      this.#logJsErrorOnce(error);
      this.#logRocketService.captureException(error);
      this.#sentryService.captureException(errorId, error);
    }

    console.error(error);
  }

  #logJsErrorOnce(error: Error): void {
    let memoizedKey = `${error.name}: ${error.message}`;

    if (error.stack) {
      memoizedKey += `\n${error.stack.split('\n').slice(0, MEMOIZED_ERROR_STACK_LINES_COUNT).join('\n')}`;
    }

    if (this.#loggedErrors.has(memoizedKey)) {
      return;
    }

    this.#loggedErrors.add(memoizedKey);
    this.#loggerService.handleJsError(error);
  }
}

export function provideErrorHandler(): Provider[] {
  return [{
    provide: ErrorHandler,
    useClass: CustomErrorHandler,
  }];
}
